import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/contextual'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {IssueSidePanel} from '@github-ui/issues-shared/IssueSidePanel'
import {LABELS} from '@github-ui/issues-shared/labels'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {useLocation} from '@github-ui/react-router'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {type ReactNode, useCallback, useEffect} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import type {SavedViewsQuery} from '../components/sidebar/__generated__/SavedViewsQuery.graphql'
import MobileNavigation from '../components/sidebar/MobileNavigation'
import {SavedViewsGraphqlQuery as customViews} from '../components/sidebar/SavedViews'
import {Sidebar} from '../components/sidebar/Sidebar'
import {ASSIGNED_TO_ME_VIEW, EMPTY_VIEW} from '../constants/view-constants'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useAppNavigate} from '../hooks/use-app-navigate'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {useRouteInfo} from '../hooks/use-route-info'
import {IssueRepoNewChoosePage, IssueRepoNewPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {
  IssueDashboardPageQuery,
  IssueDashboardPageQuery$variables,
} from './__generated__/IssueDashboardPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import {IssuesListSlots} from './IssuesListSlots'
import {List} from './List'

// Stable module-scope reference so `memo(List)` is preserved across unrelated
// re-renders. The List supplies `header`; IssuesListSlots renders the
// landmark structure.
const renderListSlotted = (parts: {header: ReactNode; filterBar: ReactNode; results: ReactNode}) => (
  <IssuesListSlots {...parts} />
)

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueDashboardPageQuery(
    $query: String = "state:open archived:false assignee:@me sort:updated-desc"
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $type: SearchType = ISSUE_ADVANCED
    $showIssueFieldPills: Boolean = false
  ) {
    ...ListQuery
      @arguments(
        query: $query
        first: $first
        labelPageSize: $labelPageSize
        skip: $skip
        fetchRepository: true
        type: $type
        showIssueFieldPills: $showIssueFieldPills
      )
  }
`

export const IssueDashboardPage: EntryPointComponent<
  {pageQuery: IssueDashboardPageQuery; customViewsQuery: SavedViewsQuery},
  Record<string, never>
> = ({queries: {pageQuery, customViewsQuery}}) => {
  const {
    sidePanelItemIdentifier,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
    onCloseSidePanel,
    onParentIssueActivate,
  } = useRouteInfo()
  const {queryRef: pageQueryRef, loadQuery} = useEntryPointsLoader<IssueDashboardPageQuery>(pageQuery, PageQuery)
  const {queryRef: savedViewsQueryRef} = useEntryPointsLoader<SavedViewsQuery>(customViewsQuery, customViews)

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
    })
  }, [])

  const {navigateToView} = useAppNavigate()
  const {search} = useLocation()

  const onSubIssueClick = useCallback(
    (subIssueItem: SubIssueSidePanelItem) => {
      const {owner, repo, number} = subIssueItem
      setSidePanelItemIdentifier({owner, repo, number, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  if (!pageQueryRef) return null
  if (!savedViewsQueryRef) return null

  const urlSearchParams = new URLSearchParams(search)
  const urlQuery = urlSearchParams.get('q')

  if (!urlQuery) {
    navigateToView({viewId: ASSIGNED_TO_ME_VIEW.id, canEditView: true})
    return null
  }

  const config: Partial<OptionConfig> = {
    shouldSkipSetDocumentTitle: true,
    onClose: onCloseSidePanel,
    insideSidePanel: true,
    showRepositoryPill: true,
    onSubIssueClick,
    onParentIssueActivate,
    navigateBack: onCloseSidePanel,
    additionalHeaderActions: (
      <IconButton
        as="a"
        role="link"
        variant="invisible"
        icon={ScreenFullIcon}
        aria-label={LABELS.sidePanelTooltip}
        href={sidePanelItemURL}
      />
    ),
  }
  const issueViewerConfig: OptionConfig = Object.assign({}, ISSUE_VIEWER_DEFAULT_CONFIG, config)

  return (
    <>
      <IssueDashboardPageContent
        pageQueryRef={pageQueryRef}
        loadQuery={loadQuery}
        savedViewsQueryRef={savedViewsQueryRef}
        onSidePanelNavigate={setSidePanelItemIdentifier}
      />
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onCloseSidePanel}>
          <IssueViewer itemIdentifier={sidePanelItemIdentifier} optionConfig={issueViewerConfig} />
        </IssueSidePanel>
      )}
    </>
  )
}

function IssueDashboardPageContent({
  pageQueryRef,
  loadQuery,
  savedViewsQueryRef,
  onSidePanelNavigate,
}: {
  pageQueryRef: PreloadedQuery<IssueDashboardPageQuery>
  savedViewsQueryRef: PreloadedQuery<SavedViewsQuery>
  loadQuery: (
    variables: IssueDashboardPageQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions | undefined,
  ) => void
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
}) {
  const setCurrentViewId = useSetCurrentViewId()

  useEffect(() => {
    setCurrentViewId(EMPTY_VIEW.id)
  }, [setCurrentViewId])

  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id: EMPTY_VIEW.id},
    {fetchPolicy: 'store-only'},
  )

  const data = usePreloadedQuery<IssueDashboardPageQuery>(PageQuery, pageQueryRef)

  if (!currentViewNode || !currentViewNode.node) return null

  const filteredListLayoutEnabled = isFeatureEnabled('global_issues_filtered_list_layout')

  const list = (
    <List
      fetchedView={currentViewNode.node}
      fetchedRepository={null}
      search={data}
      loadSearchQuery={loadQuery}
      onSidePanelNavigate={onSidePanelNavigate}
      showSsoBanner={false}
      renderSlotted={filteredListLayoutEnabled ? renderListSlotted : undefined}
    />
  )

  if (filteredListLayoutEnabled) {
    return (
      <AnalyticsWrapper category="Issues Dashboard">
        <ContextualGlobalSsoBanner />
        <FilteredListPageLayout>
          <FilteredListPageLayout.Sidebar aria-label={LABELS.viewSidebarPaneAriaLabel} dialogTitle="Issues">
            <Sidebar customViewsRef={savedViewsQueryRef} />
          </FilteredListPageLayout.Sidebar>
          <FilteredListPageLayout.Content as="div">{list}</FilteredListPageLayout.Content>
        </FilteredListPageLayout>
        <MobileNavigation customViewsRef={savedViewsQueryRef} />
      </AnalyticsWrapper>
    )
  }

  return (
    <AnalyticsWrapper category="Issues Dashboard">
      <ContextualGlobalSsoBanner />
      <ThreePanesLayout
        leftPaneWidth={'small'}
        leftPane={{
          element: <Sidebar customViewsRef={savedViewsQueryRef} />,
          ariaLabel: LABELS.viewSidebarPaneAriaLabel,
        }}
        middlePane={list}
      />
      <MobileNavigation customViewsRef={savedViewsQueryRef} />
    </AnalyticsWrapper>
  )
}
